<template>
    <div
        class="animated-glow"
        :class="{
            'animated-glow--fill': fill,
            'animated-glow--inline': inline,
            'animated-glow--sides': sides,
        }"
        aria-hidden="true"
    >
        <!-- Fill backgrounds for smooth cross-fade -->
        <template v-if="fill">
            <div
                class="animated-glow__fill"
                :class="[
                    `animated-glow__fill--${fillFrameA}`,
                    { 'is-visible': showA },
                ]"
            ></div>
            <div
                class="animated-glow__fill"
                :class="[
                    `animated-glow__fill--${fillFrameB}`,
                    { 'is-visible': !showA },
                ]"
            ></div>
        </template>
        <img
            class="animated-glow__frame"
            :src="srcA"
            :class="{ 'is-visible': showA }"
            alt=""
        />
        <img
            class="animated-glow__frame"
            :src="srcB"
            :class="{ 'is-visible': !showA }"
            alt=""
        />
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

import frame1 from "@/assets/img/animated-glow/1.png";
import frame2 from "@/assets/img/animated-glow/2.png";
import frame3 from "@/assets/img/animated-glow/3.png";
import frame4 from "@/assets/img/animated-glow/4.png";

const frames = [frame1, frame2, frame3, frame4];

const srcA = ref(frames[0]);
const srcB = ref(frames[1]);
const showA = ref(true);
const fillFrameA = ref(1);
const fillFrameB = ref(2);
defineProps({
    sides: {
        type: Boolean,
        default: true,
    },
    fill: {
        type: Boolean,
        default: false,
    },
    inline: {
        type: Boolean,
        default: false,
    },
});
let idx = 0;
let timer = null;

function preload() {
    try {
        frames.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    } catch (_) {
        // no-op
    }
}

function tick() {
    const nextIdx = (idx + 1) % frames.length;
    const nextSrc = frames[nextIdx];
    const nextFillFrame = nextIdx + 1; // 1-indexed for CSS classes

    // Update the currently hidden layer, then flip visibility for a cross-fade.
    if (showA.value) {
        srcB.value = nextSrc;
        fillFrameB.value = nextFillFrame;
    } else {
        srcA.value = nextSrc;
        fillFrameA.value = nextFillFrame;
    }

    showA.value = !showA.value;
    idx = nextIdx;
}

onMounted(() => {
    preload();

    // Includes the fade time below; tweak if you want faster/slower.
    const cycleMs = 800;
    timer = window.setInterval(tick, cycleMs);
});

onBeforeUnmount(() => {
    if (timer) window.clearInterval(timer);
});
</script>

<style scoped>
.animated-glow {
    position: fixed;
    inset: 0;
    pointer-events: none;
    user-select: none;

    /* match the phone/card rounding */
    border-radius: inherit;

    /* ensure it sits behind in-flow content within the parent stacking context */
    z-index: 10;
    max-height: calc(100vh - 2rem);
    top: 1rem;
    bottom: 1rem;
}
.animated-glow--inline {
    position: absolute;
    z-index: 10;
    max-height: none;
    top: 0;
    bottom: 0;
}
.animated-glow__frame {
    position: absolute;

    width: 110.8%;
    height: calc(100% + 4rem);
    top: -2rem;
    border-radius: inherit;

    /* fill the parent; frames are designed as a border with transparent center */
    object-fit: fill;

    opacity: 0;
    transition: opacity 600ms ease-in-out;
    max-width: none;
    left: -5.2%;
}

.animated-glow__frame.is-visible {
    opacity: 1;
}

/* Fill elements for smooth cross-fade between gradient backgrounds */
.animated-glow__fill {
    position: absolute;
    inset: -2rem;
    inset-inline: -5%;
    border-radius: inherit;
    z-index: -2;
    pointer-events: none;
    opacity: 0;
    transition: opacity 600ms ease-in-out;
    /*
     * SVG mask with rounded rectangle cutout.
     * The inner rect is positioned to match the phone area within the fill element.
     * Adjust x, y, width, height, rx values to tune the cutout shape.
     */
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Cdefs%3E%3Cmask id='hole'%3E%3Crect width='100%25' height='100%25' fill='white'/%3E%3Crect x='4.5%25' y='3%25' width='91%25' height='94%25' rx='53' fill='black'/%3E%3C/mask%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='white' mask='url(%23hole)'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Cdefs%3E%3Cmask id='hole'%3E%3Crect width='100%25' height='100%25' fill='white'/%3E%3Crect x='4.5%25' y='3%25' width='91%25' height='94%25' rx='53' fill='black'/%3E%3C/mask%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='white' mask='url(%23hole)'/%3E%3C/svg%3E");
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
}

.animated-glow__fill.is-visible {
    opacity: 1;
}

.animated-glow__fill--1 {
    background: radial-gradient(
        ellipse at center,
        #bc82f3 17%,
        #f5b9ea 24%,
        #8d99ff 35%,
        #aa6eee 58%,
        #ff6778 70%,
        #ffba71 81%,
        #c686ff 92%
    );
}

.animated-glow__fill--2 {
    background: radial-gradient(
        ellipse at center,
        #aa6eee 8%,
        #8d99ff 14%,
        #f5b9ea 26%,
        #ffba71 31%,
        #bc82f3 33%,
        #c686ff 42%,
        #ff6778 70%
    );
}

.animated-glow__fill--3 {
    background: radial-gradient(
        ellipse at center,
        #c686ff 8%,
        #f5b9ea 16%,
        #ffba71 29%,
        #aa6eee 32%,
        #ff6778 41%,
        #bc82f3 57%,
        #8d99ff 75%
    );
}

.animated-glow__fill--4 {
    background: radial-gradient(
        ellipse at center,
        #c686ff 8%,
        #ffba71 19%,
        #ff6778 30%,
        #aa6eee 42%,
        #8d99ff 64%,
        #f5b9ea 76%,
        #bc82f3 83%
    );
}
.animated-glow--sides {
    max-height: none;
    top: -10rem;
    bottom: -10rem;
}
</style>
